import {PointOfInterestDTO} from '../dto/point-of-interest-dto';
import {Storable} from '../interfaces/storable';
import {Address} from './address';
import {ImageConversions} from './image-conversions';
import {Category} from './category';
import {Social} from './social';
import {Role} from '../enums/role';
import {Seo} from './seo';
import {GoldenTip} from './golden-tip';

export class PointOfInterest extends Storable<PointOfInterestDTO> {
  private readonly _id: number;
  private readonly _slug: string;
  private readonly _title: string;
  private readonly _externalUrl: string | null;
  private readonly _address: Address;
  private readonly _phoneNumber: string | null;
  private readonly _phoneNumberMobile: string | null;
  private readonly _description: string | null;
  private readonly _latitude: string;
  private readonly _longitude: string;
  private readonly _categoryIds: number[];
  private _categories?: Category[];
  private readonly _mainCategoryId: number;
  private _mainCategory?: Category;
  private readonly _isAmbassador: boolean;
  private readonly _isThemeLocation: boolean;
  private readonly _role: Role;
  private readonly _image: ImageConversions;
  private readonly _socials: Social[];
  private readonly _goldenTips: GoldenTip[];
  private readonly _seo?: Seo;

  constructor(data: PointOfInterestDTO) {
    super();
    this._id = data.id;
    this._slug = data.slug;
    this._title = data.title;
    this._externalUrl = data.external_url;
    this._address = new Address(data.address);
    this._phoneNumber = data.phone_number;
    this._phoneNumberMobile = data.phone_number_mobile;
    this._description = data.description;
    this._latitude = data.latitude;
    this._longitude = data.longitude;
    this._categoryIds = data.category_ids;
    this._mainCategoryId = data.main_category_id;
    this._isAmbassador = data.is_ambassador;
    this._isThemeLocation = data.is_theme_location;
    this._role = data.role;
    this._image = new ImageConversions(data.image);
    this._socials = (data.socials ?? []).map(social => new Social(social));
    this._goldenTips = data.golden_tips.map(goldenTip => new GoldenTip(goldenTip));
    if (data.seo) {
      this._seo = new Seo(data.seo);
    }
  }

  get id() {
    return this._id;
  }

  get slug(): string {
    return this._slug;
  }

  get title() {
    return this._title;
  }

  get externalUrl() {
    return this._externalUrl;
  }

  get address() {
    return this._address;
  }

  get phoneNumber() {
    return this._phoneNumber;
  }

  get phoneNumberMobile() {
    return this._phoneNumberMobile;
  }

  get description() {
    return this._description;
  }

  get latitude() {
    return this._latitude;
  }

  get longitude() {
    return this._longitude;
  }

  get categoryIds() {
    return this._categoryIds;
  }

  get isAmbassador() {
    return this._isAmbassador;
  }

  get isThemeLocation() {
    return this._isThemeLocation;
  }

  get role() {
    if (this.isAmbassador) {
      return Role.AMBASSADOR;
    }
    return this._role;
  }

  get shouldShowImage() {
    if (this.isThemeLocation) {
      return true;
    }
    if (this.isAmbassador) {
      return true;
    }
    if (this.role === undefined) {
      return false;
    }
    return this.role !== Role.EXPLORER;
  }

  get image() {
    return this._image;
  }

  get socials() {
    return this._socials;
  }

  get goldenTips() {
    return this._goldenTips;
  }

  get seo() {
    return this._seo;
  }

  get googleMapsLatLngLiteral(): google.maps.LatLngLiteral {
    return {
      lat: Number(this._latitude),
      lng: Number(this._longitude)
    };
  }

  set categories(categories: Category[]) {
    this._categories = categories.filter(category => this._categoryIds.includes(category.id));
    this._mainCategory = categories.find(category => category.id === this._mainCategoryId);
  }

  get categories(): Category[] | undefined {
    return this._categories;
  }

  get mainCategoryId(): number {
    return this._mainCategoryId;
  }

  get mainCategory(): Category | undefined {
    return this._mainCategory;
  }

  getDTO(): PointOfInterestDTO {
    return {
      id: this._id,
      slug: this._slug,
      title: this._title,
      ['external_url']: this._externalUrl,
      address: this._address.getDTO(),
      ['phone_number']: this._phoneNumber,
      ['phone_number_mobile']: this._phoneNumberMobile,
      description: this._description,
      latitude: this._latitude,
      longitude: this._longitude,
      ['golden_tips']: this._goldenTips.map(goldenTip => goldenTip.getDTO()),
      ['category_ids']: this._categoryIds,
      ['main_category_id']: this._mainCategoryId,
      ['is_ambassador']: this._isAmbassador,
      ['is_theme_location']: this._isThemeLocation,
      role: this._role,
      image: this._image.getDTO(),
      socials: this._socials.map(social => social.getDTO()),
    };
  }
}
